<template>
    <Container>
        <SearchInput
            v-model="searchText"
            :loading="isFirstLoad" />
        <template v-if="!searchText">
            <SubTitle class="tw-mt-6">{{ $t('search.categories') }}</SubTitle>
            <CategoriesGrid class="tw-mt-5">
                <CategorySlot
                    v-for="category in filteredCategories"
                    :key="category.value"
                    :category="category" />
            </CategoriesGrid>
        </template>
        <template v-else>
            <template v-if="posts.length > 0">
                <SubTitle class="tw-my-5 tw-text-gray-400">{{ $t('search.news') }}</SubTitle>
                <PostList
                    v-if="posts.length > 0"
                    class="tw-px-0"
                    :posts="posts" />
                <!-- see more button -->
                <!-- v-if="newsLimited.length < news.length" -->
                <SeeMoreButton
                    v-if="totalPosts > posts.length"
                    class="tw-mx-auto tw-mt-5"
                    :loading="isFetchingMore"
                    @click="fetchMore">
                </SeeMoreButton>
            </template>
            <template v-if="isFirstLoad">
                <SubTitle class="tw-my-5 tw-w-fit tw-animate-pulse tw-rounded-lg tw-bg-gray-300 tw-text-gray-300">{{ $t('search.news') }}</SubTitle>
                <template
                    v-for="i in 4"
                    :key="i">
                    <PostListItemLoader class="tw-animate-pulse"></PostListItemLoader>
                    <v-divider
                        v-if="i < 4"
                        class="tw-my-5"
                        opacity="0.1"></v-divider>
                </template>
            </template>
        </template>
    </Container>
</template>

<script setup lang="ts">
    import { searchPosts } from '@/api/posts';
    import categories from '@/configs/categories';
    import type { WPPost } from '@/contentTypes';
    import PostListItemLoader from '@/components/loaders/news/PostListItemLoader.vue';
    import { useOnline } from '@vueuse/core';
    import { useIndexedDBStore } from '@/composables/useIndexDB';
    import { PAGINATION_CONSTANTS, UI_CONSTANTS } from '@/configs/constants';
    import { sanitizeSearchQuery } from '@/utils/sanitize';
import { toRaw } from 'vue';

    const isOnline = useOnline();
    const postsStore = useIndexedDBStore<WPPost>('posts');

    // Utility to search posts in IndexedDB when offline using composable
    async function searchPostsIndexedDB(query: { search: string; per_page: number; page: number }) {
        const allPosts = await postsStore.getAll();
        const sanitizedSearch = sanitizeSearchQuery(query.search);
        const filtered = allPosts.filter((post) =>
            (post.title || '').toLowerCase().includes(sanitizedSearch.toLowerCase())
        );
        const start = (query.page - 1) * query.per_page;
        const paged = filtered.slice(start, start + query.per_page);
        return { posts: paged, pagination: { total: filtered.length } };
    }

    const searchText = ref('');
    const posts = ref<WPPost[]>([]);

    const perPage = PAGINATION_CONSTANTS.DEFAULT_POSTS_PER_PAGE;
    const page = ref(1);
    const totalPosts = ref(0);
    const timeoutId = ref<ReturnType<typeof setTimeout> | null>(null);
    const isLoading = ref(false);
    const currentRequestId = ref<number>(0);

    const isFetchingMore = computed(() => {
        return isLoading.value && posts.value.length > 0;
    });

    const isFirstLoad = computed(() => {
        return posts.value.length === 0 && isLoading.value;
    });

    const filteredCategories = computed(() => {
        return categories.filter((category) => category.showInSearch);
    });

    // const isFirstLoad = ref(true)

    async function search() {
        if (searchText.value.length < 1) {
            posts.value = [];
            return;
        }
        isLoading.value = true;

        // Generate a unique request ID for this search
        const requestId = toRaw(++currentRequestId.value);

        const query = {
            search: sanitizeSearchQuery(toRaw(searchText.value)),
            per_page: perPage,
            page: toRaw(page.value),
        };

        try {
            let data;
            if (isOnline.value) {
                // Online: use API with request ID tracking
                ({ data } = await searchPosts(query));
            } else {
                // Offline: use IndexedDB composable
                data = await searchPostsIndexedDB(query);
            }

            // Check if this is still the current request (prevent race conditions)
            if (requestId !== toRaw(currentRequestId.value)) {
                // This is an outdated request, ignore the response
                return;
            }

            if (data.pagination && data.pagination.total) {
                totalPosts.value = data.pagination.total;
            } else {
                totalPosts.value = 0; // Reset total posts if no pagination data
            }
            if (data.posts && data.posts.length > 0) {
                if (page.value === 1) {
                    posts.value = data.posts; // Reset posts on new search
                } else {
                    posts.value.push(...data.posts); // Append new posts
                }
            } else {
                posts.value = [];
            }
            isLoading.value = false;
        } catch (err: any) {
            // Only handle errors if this is still the current request
            if (requestId === toRaw(currentRequestId.value)) {
                console.error('Error searching posts:', err);
                posts.value = [];
                totalPosts.value = 0;
                isLoading.value = false;
            }
        }
    }

    function fetchMore() {
        page.value++;
        search();
    }

    watch(searchText, (newValue) => {
        if (newValue.length >= 1) {
            page.value = 1; // Reset page to 1 on new search
            posts.value = []; // Clear previous results
            totalPosts.value = 0; // Reset total posts count
            if (timeoutId.value) {
                clearTimeout(timeoutId.value);
            }
            // Increment request ID to invalidate previous requests
            currentRequestId.value++;
            timeoutId.value = setTimeout(() => {
                search();
            }, UI_CONSTANTS.SEARCH_DEBOUNCE_MS); // Debounce search input
        }
    });

    search();
</script>

<style scoped></style>
